import { api } from '@/api/client'
import { getDepartmentsForServices } from '@/shared/constants/services'

export function deriveProjectKey(title: string, existingKeys: string[] = []): string {
  if (!title) return 'PROJ'
  // Remove "Deal:" prefix so we get meaningful acronyms from the actual deal name/client
  const cleanTitle = title.replace(/^deal:\s*/i, '').trim() || title.trim()
  const words = cleanTitle.split(/\s+/).filter(Boolean)
  let baseKey = 'PROJ'
  if (words.length >= 2) {
    const acronym = words
      .map((w) => w.replace(/[^a-zA-Z0-9]/g, '')[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
    if (acronym.length >= 2) {
      baseKey = acronym.slice(0, 5)
    }
  } else if (words.length === 1) {
    const clean = words[0].replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
    baseKey = clean.slice(0, 4) || 'PROJ'
  }
  if (baseKey.length < 2) {
    baseKey = (baseKey + 'PR').slice(0, 4)
  }

  // Ensure candidate is unique among existing keys
  let candidate = baseKey
  let counter = 2
  const upperExisting = new Set(existingKeys.map((k) => k.toUpperCase()))
  while (upperExisting.has(candidate) && counter <= 99) {
    const prefix = baseKey.slice(0, 8)
    candidate = `${prefix}${counter}`
    counter++
  }
  return candidate
}

/**
 * The name a deal gets: "Deal: <name> (<company>)". Deals converted from a lead
 * are named this way; the add-deal form uses it too so every deal reads alike.
 * A name that already starts with "Deal:" is left as is.
 */
export function formatDealTitle(name: string, company?: string | null): string {
  const n = name.trim()
  if (/^deal:/i.test(n)) return n
  const c = (company || '').trim()
  return c && c !== '—' && c !== '--' ? `Deal: ${n} (${c})` : `Deal: ${n}`
}

export interface LinkedProjectInfo {
  id: string
  key: string
  name: string
  isDealWon?: boolean
}

/**
 * Fetches all projects for the organization and maps sourceDealId -> Project
 */
export async function fetchLinkedProjects(orgId?: string): Promise<Map<string, LinkedProjectInfo>> {
  const map = new Map<string, LinkedProjectInfo>()
  if (!orgId) return map
  try {
    const projects: any[] = await api.get('/projects', { organizationId: orgId })
    for (const p of projects) {
      const dealIdStr = p.sourceDealId ? String((p.sourceDealId as any)._id || p.sourceDealId) : null
      if (dealIdStr) {
        map.set(dealIdStr, { id: p.id, key: p.key, name: p.name, isDealWon: p.isDealWon })
      }
    }
  } catch (err) {
    console.warn('Failed to fetch linked projects for deals:', err)
  }
  return map
}

/**
 * Creates a project in PostgreSQL/Prisma corresponding to a Won Deal
 */
export async function createProjectFromDeal(
  deal: {
    id: string
    title: string
    client?: string | null
    notes?: string | null
    value?: number | string | null
    owner?: string | null
    closeDate?: string | null
    contactPerson?: string | null
    contactEmail?: string | null
    contactPhone?: string | null
    probability?: number | string | null
    services?: string[] | null
    clientAddress?: string | null
    clientGstin?: string | null
  },
  orgId?: string
): Promise<{ success: boolean; project?: any; error?: string }> {
  let resolvedOrgId = orgId
  if (!resolvedOrgId && typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem('goodday_organization') || localStorage.getItem('organization')
      if (stored) {
        const parsed = JSON.parse(stored)
        resolvedOrgId = parsed.id || parsed._id
      }
    } catch {}
  }

  if (!resolvedOrgId) {
    return { success: false, error: 'Organization not loaded yet — please try again in a moment.' }
  }

  let existingKeys: string[] = []
  try {
    const existingProjects: any[] = await api.get('/projects', { organizationId: resolvedOrgId })
    existingKeys = existingProjects.map((p) => p.key).filter(Boolean)
    // If project already exists for this deal, return it immediately
    const found = existingProjects.find(
      (p) => p.sourceDealId && String(p.sourceDealId._id || p.sourceDealId) === deal.id
    )
    if (found) {
      return {
        success: true,
        project: { id: found.id || found._id, name: found.name, key: found.key },
      }
    }
  } catch {}

  const key = deriveProjectKey(deal.title, existingKeys)

  const detailsParts: string[] = []
  if (deal.client) detailsParts.push(`Client: ${deal.client}`)
  if (deal.services && deal.services.length > 0) {
    detailsParts.push(`Services: ${deal.services.join(', ')}`)
  }
  if (deal.value) {
    const numVal = typeof deal.value === 'number' ? deal.value : parseFloat(String(deal.value))
    detailsParts.push(`Deal Value: ₹${isNaN(numVal) ? deal.value : numVal.toLocaleString('en-IN')}`)
  }
  if (deal.owner) detailsParts.push(`Account Owner: ${deal.owner}`)
  if (deal.contactPerson || deal.contactEmail || deal.contactPhone) {
    const contactInfo = [deal.contactPerson, deal.contactEmail, deal.contactPhone].filter(Boolean).join(' | ')
    detailsParts.push(`Client Contact: ${contactInfo}`)
  }
  if (deal.clientAddress) detailsParts.push(`Client Address: ${deal.clientAddress}`)
  if (deal.clientGstin) detailsParts.push(`Client GSTIN: ${deal.clientGstin}`)
  if (deal.closeDate) detailsParts.push(`Target Delivery / Close Date: ${deal.closeDate}`)
  if (deal.notes) detailsParts.push(`\nNotes:\n${deal.notes}`)

  const description =
    detailsParts.length > 0
      ? detailsParts.join('\n')
      : `Project created from deal: ${deal.title} (${deal.client || 'Client'})`

  try {
    const project = await api.post<{ id: string; name: string; key: string }>(
      '/projects',
      {
        name: deal.title,
        key,
        description,
        color: '#10b981',
        type: 'kanban',
        projectType: 'software',
        departments: getDepartmentsForServices(deal.services),
        sourceDealId: deal.id,
        isDealWon: true,
        services: deal.services || [],
      },
      { params: { organizationId: resolvedOrgId } }
    )

    if (project?.id) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('projectCreated', {
            detail: { project, dealId: deal.id },
          })
        )
      }
      return { success: true, project }
    }
    return { success: false, error: 'Project creation returned no data' }
  } catch (err: any) {
    console.error('Error creating project from deal:', err)
    const serverMsg = err.response?.data?.message || err.message || 'Failed to create project'
    let msg = serverMsg
    if (
      msg.includes('E11000') ||
      msg.includes('duplicate key') ||
      msg.includes('already exists') ||
      msg.includes('already created')
    ) {
      msg = `The project "${deal.title}" [${key}] is already created or its key is in use.`
    }
    // A permission failure from the API reads as a raw 'Forbidden: ...' string.
    if (msg.includes('Forbidden')) msg = "You don't have permission to create projects."
    return { success: false, error: msg }
  }
}
